import React from 'react';
import { Table, TableHead, TableRow, TableBody } from '@mui/material';
import { IProject, ITeamMember } from '@types';
import { HEADER_WIDTHS, TeamsTableHeader } from '../constants/headers';
import { TableHeaderCell } from '../../common/TableHeaderCell';
import { ProjectToggleRow } from './ProjectToggleRow';
import { MemberRow } from './MemberRow';
import { compareMembers } from '../utils/sorting';
import { navRailBreakpoint } from '@theme/variables';
import { GroupFold } from '@hooks';
import {
  HEADER_LINE_HEIGHT,
  HEADER_PADDING_Y,
  headerStickyTop,
} from '../constants/sticky';

type Props = {
  projects: IProject[];
  headers: TeamsTableHeader[];
  sortKey: TeamsTableHeader;
  sortDir: 'asc' | 'desc';
  onSort: (key: TeamsTableHeader) => void;
  accountDeliveryManager?: ITeamMember;
  fold: GroupFold;
};

export const TeamsTable: React.FC<Props> = ({
  projects,
  headers,
  sortKey,
  sortDir,
  onSort,
  accountDeliveryManager,
  fold,
}) => {
  return (
    <Table>
      <TableHead
        sx={{
          position: { xs: 'static', [navRailBreakpoint]: 'sticky' },
          top: headerStickyTop,
          backgroundColor: 'white',
          zIndex: 100,
        }}
      >
        <TableRow
          sx={{
            '& > .MuiTableCell-root': {
              border: 'none',
              py: HEADER_PADDING_Y,
              lineHeight: HEADER_LINE_HEIGHT,
              px: { [navRailBreakpoint]: '12px' },
            },
            '& .MuiTypography-root': {
              whiteSpace: { xs: 'normal', [navRailBreakpoint]: 'nowrap' },
            },
          }}
        >
          {headers.map((label) => (
            <TableHeaderCell
              key={label}
              label={label}
              sortKey={label}
              activeSortKey={sortKey}
              sortDir={sortDir}
              onSort={(key) => onSort(key as TeamsTableHeader)}
              width={HEADER_WIDTHS[label]}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {projects.map(({ name, id, members, deliveryManager }) => {
          const dm = deliveryManager ?? accountDeliveryManager;
          const sorted = [...members].sort((a, b) =>
            compareMembers(
              {
                ...a,
                deliveryManager: dm,
              },
              {
                ...b,
                deliveryManager: dm,
              },
              sortKey,
              sortDir,
            ),
          );
          return (
            <React.Fragment key={name}>
              <ProjectToggleRow
                name={name}
                count={members.length}
                colSpan={headers.length}
                {...fold(id)}
              />
              {!fold(id).collapsed &&
                sorted.map((member, index) => (
                  <MemberRow
                    visibleHeaders={headers}
                    key={`${name}-${member.fullName}`}
                    member={{
                      ...member,
                      deliveryManager: dm,
                    }}
                    projectId={id}
                    isLast={index === sorted.length - 1}
                  />
                ))}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
